import {Container, DisplayObject, Graphics, MaskData} from 'pixi.js';
import {Rectangle} from '@gongxigames/framework/core/fragments/geom/Rectangle';
import {DestroyOptions} from '@gongxigames/framework/core/types';
import {FlexBoxLayout, FlexBoxLayoutOptions} from '@gongxigames/framework/core/fragments/display/FlexBox/FlexBoxLayout';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';

export type FlexBoxOptions = FlexBoxLayoutOptions & {
    debug?: boolean;
};
export class FlexBox extends Container {
    protected options: FlexBoxOptions = {
        direction: 'row',
        justifyContent: 'start',
        alignItems: 'start',
        padding: {top: 0, bottom: 0, left: 0, right: 0},
        gap: 0,
        wrap: false,
        containerWidth: 0,
        containerHeight: 0
    };

    protected layout: FlexBoxLayout;
    protected overlay: Graphics = new Graphics();
    protected cachedContentItemsRects: Rectangle[] = [];
    private updateCallback: (dt: number) => void = this.update.bind(this);

    constructor(
        options: Partial<FlexBoxOptions>,
        private readonly updateManager: UpdateManager
    ) {
        super();
        this.setOptions(options);
        this.layout = new FlexBoxLayout(this.options, []);

        this.on('added', this.onAdded.bind(this));
        this.on('removed', this.onRemoved.bind(this));

        this.name = 'FlexBox';
    }

    protected onAdded(): void {
        this.update();
        this.updateManager.addUpdateFunction(this.updateCallback);
    }

    protected onRemoved(): void {
        this.updateManager.removeUpdateFunction(this.updateCallback);
    }

    public update(): void {
        if (this._destroyed) {
            return;
        }

        const bounds = this.getContentItemsRects();
        const children = this.getChildren();

        if (!this.layout.isOptionsEqual(this.options) || !this.isEqualChildrenBounds(bounds)) {
            this.updateLayout(children, bounds);
            this.cachedContentItemsRects = this.getContentItemsRects();
        }

        if (this.options.debug) {
            this.drawOverlay();
        } else if (this.overlay.parent) {
            this.destroyOverlay();
        }
    }

    protected updateLayout(items: DisplayObject[], childrenBounds: Rectangle[]): void {
        this.layout.update(this.options, childrenBounds);
        this.layout.getContent().forEach((item, i) => {
            const displayObject = items[i];
            const localBounds = !(displayObject instanceof FlexBox) ? displayObject.getLocalBounds() : new Rectangle();

            displayObject.x = item.x - localBounds.x;
            displayObject.y = item.y - localBounds.y;
        });

        Promise.resolve().then(() => {
            this.emit('updated', this.layout.getBoundsRect().ceil());
        });
    }

    protected isEqualChildrenBounds(bounds: Rectangle[]): boolean {
        const equalBounds = (rectangle: Rectangle, i: number): boolean => rectangle.isEqual(this.cachedContentItemsRects[i]);

        return bounds.length === this.cachedContentItemsRects.length && bounds.every(equalBounds);
    }

    protected getContentItemsRects(): Rectangle[] {
        return this.getChildren().map(item => {
            const rect = item.getLocalBounds().clone();

            return new Rectangle(
                Math.trunc(rect.x),
                Math.trunc(rect.y),
                Math.trunc(rect.width),
                Math.trunc(rect.height)
            );
        });
    }

    public updateOptions(options: Partial<FlexBoxOptions>): void {
        this.setOptions(options);
        this.update();
    }

    protected setOptions(options: Partial<FlexBoxOptions>): void {
        this.options = {
            ...this.options,
            ...options
        };
    }

    protected drawOverlay(): void {
        const {x, y, width, height} = this.layout.getBoundsRect();

        if (!this.overlay.parent) {
            this.addChildAt(this.overlay, this.children.length);
        }

        this.overlay.clear();
        this.overlay.beginFill(0x00ff00, 0.2);
        this.overlay.drawRect(x, y, width, height);
        this.overlay.endFill();
    }

    public getChildren(): DisplayObject[] {
        return this.children.filter(child => child !== this.overlay);
    }

    protected destroyOverlay(): void {
        if (this.overlay.parent) {
            this.removeChild(this.overlay);
        }
    }

    public destroy(options?: DestroyOptions): void {
        this.updateManager.removeUpdateFunction(this.updateCallback);

        super.destroy(options);
    }

    public calculateBounds(): void {
        this._bounds.clear();
        this._calculateBounds();

        const children = this.getChildren();

        for (const oneChild of children) {
            const child = oneChild as DisplayObject & {calculateBounds: () => void; _mask: MaskData | null;};

            if (!child.visible || !child.renderable) {
                continue;
            }

            child.calculateBounds();

            if (child._mask) {
                const maskObject = ((child._mask as MaskData).maskObject || child._mask) as Container;

                maskObject.calculateBounds();
                this._bounds.addBoundsMask(child._bounds, maskObject._bounds);
            } else if (child.filterArea) {
                this._bounds.addBoundsArea(child._bounds, child.filterArea);
            } else {
                this._bounds.addBounds(child._bounds);
            }
        }
        const {padding} = this.options;

        this._bounds.addFramePad(
            this._bounds.minX - padding.left,
            this._bounds.minY - padding.top,
            this._bounds.maxX + padding.right,
            this._bounds.maxY + padding.bottom,
            0, 0);

        this._bounds.updateID = this._boundsID;
    }
}
